import type { WorkspaceAppearance, WorkspaceCabinet, WorkspacePaletteAccent, WorkspacePaletteBackground } from "@wms/domain";
import { tokens } from "@wms/ui";

/**
 * ТЗ «Рабочие столы и персонализация» 31.10.2026 (UXW-10, UXW-14, CW-04…06): the own look is a pair of ids of the
 * approved palette; every colour the screen needs (hover, pressed, link, selected, focus) is derived here from them,
 * and a combination whose contrast is too low is never applied nor saved. Pure functions only.
 */
export interface Palette {
  backgrounds: WorkspacePaletteBackground[];
  accents: WorkspacePaletteAccent[];
}

/** «Стандартный» of staff: the neutral background of the light theme of today, not an entry of the palette. */
export const STANDARD_BACKGROUND = "standard";
export const STANDARD_CANVAS = tokens.color.background;
/** The text and the secondary text of the light theme (`colorText`, `--wms-muted`). */
export const TEXT_COLOR = tokens.color.text;
export const MUTED_COLOR = tokens.color.textSecondary;
const WHITE = "#ffffff";
const BLACK = "#000000";

/** WCAG 2.2, 1.4.3 / 1.4.11: normal text 4.5:1; borders, focus and large text 3:1. */
export const MIN_TEXT = 4.5;
export const MIN_UI = 3;

// UXW-17: a copy of the palette of `wms/common/workspace.py` for the moment before the server answers; the palette of
// the server answer always wins
export const FALLBACK_PALETTE: Palette = {
  backgrounds: [
    { id: "lilac", label: "Сирень", hex: "#F3EEFC" },
    { id: "mint", label: "Мята", hex: "#EDF8F1" },
    { id: "sky", label: "Небо", hex: "#EDF5FC" },
    { id: "peach", label: "Персик", hex: "#FFF3E8" },
    { id: "sand", label: "Песок", hex: "#F9F4E9" },
    { id: "rose", label: "Роза", hex: "#FCEFF3" },
    { id: "ice", label: "Лёд", hex: "#EFF8F8" },
    { id: "light", label: "Светлый", hex: "#F8FAFC" },
  ],
  accents: [
    { id: "blue", label: "Синий", hex: "#2F6FED", text: "#FFFFFF" },
    { id: "lilac", label: "Сиреневый", hex: "#65459C", text: "#FFFFFF" },
    { id: "green", label: "Зелёный", hex: "#21684B", text: "#FFFFFF" },
    { id: "teal", label: "Бирюзовый", hex: "#126A72", text: "#FFFFFF" },
    { id: "graphite", label: "Графит", hex: "#374151", text: "#FFFFFF" },
  ],
};

// UXW-10: the cabinet of a client is light lilac by default, staff keeps the neutral background (the same as the server)
export const DEFAULT_APPEARANCE: Record<WorkspaceCabinet, WorkspaceAppearance> = {
  staff: { background: STANDARD_BACKGROUND, accent: "blue" },
  portal: { background: "lilac", accent: "blue" },
};

export function parseHex(hex: string): [number, number, number] | null {
  const match = /^#?([0-9a-f]{6}|[0-9a-f]{3})$/i.exec(hex.trim());
  if (!match) return null;
  const digits = match[1].length === 3 ? match[1].replace(/./g, (c) => c + c) : match[1];
  return [0, 2, 4].map((i) => parseInt(digits.slice(i, i + 2), 16)) as [number, number, number];
}

function toHex(rgb: number[]): string {
  return "#" + rgb.map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, "0")).join("");
}

/** WCAG relative luminance of an sRGB colour (0 — black, 1 — white). */
export function relativeLuminance(hex: string): number {
  const rgb = parseHex(hex);
  if (!rgb) return 0;
  const [r, g, b] = rgb.map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio of two colours, from 1 to 21. */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** The ratio as people read it: «4,55:1» (rounded down, so 4,49 never shows as 4,5). */
export function formatRatio(ratio: number): string {
  return `${(Math.floor(ratio * 100) / 100).toFixed(2).replace(".", ",")}:1`;
}

/** `a` moved towards `b` by `t` (0 — a, 1 — b). */
export function mix(a: string, b: string, t: number): string {
  const x = parseHex(a) ?? [0, 0, 0];
  const y = parseHex(b) ?? [0, 0, 0];
  return toHex(x.map((v, i) => v + (y[i] - v) * t));
}

export function rgba(hex: string, alpha: number): string {
  const [r, g, b] = parseHex(hex) ?? [0, 0, 0];
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** The colour darkened in small steps until it reaches `min` against every given background (the link ink). */
export function readableOn(color: string, backgrounds: string[], min = MIN_TEXT): string {
  let result = color;
  for (let step = 1; step <= 20 && backgrounds.some((bg) => contrastRatio(result, bg) < min); step++) {
    result = mix(color, BLACK, step * 0.05);
  }
  return result;
}

export interface ResolvedLook {
  /** The ids actually shown: an unknown id falls back to the standard of the cabinet (CW-04). */
  background: string;
  accent: string;
  backgroundLabel: string;
  accentLabel: string;
  /** null — the standard background of the theme («Стандартный» of staff). */
  canvas: string | null;
  accentHex: string;
  accentText: string;
}

/** The ids of a saved or previewed look → colours of the palette; an unknown id gives the standard of the cabinet. */
export function resolveAppearance(
  appearance: WorkspaceAppearance | null | undefined,
  cabinet: WorkspaceCabinet,
  palette: Palette = FALLBACK_PALETTE,
): ResolvedLook {
  const fallback = DEFAULT_APPEARANCE[cabinet];
  const findBackground = (id: string | undefined) => palette.backgrounds.find((b) => b.id === id) ?? FALLBACK_PALETTE.backgrounds.find((b) => b.id === id);
  const findAccent = (id: string | undefined) => palette.accents.find((a) => a.id === id) ?? FALLBACK_PALETTE.accents.find((a) => a.id === id);
  const wantedBackground = appearance?.background ?? fallback.background;
  const standard = wantedBackground === STANDARD_BACKGROUND;
  const background = standard ? null : findBackground(wantedBackground) ?? (fallback.background === STANDARD_BACKGROUND ? null : findBackground(fallback.background));
  const accent = findAccent(appearance?.accent) ?? findAccent(fallback.accent) ?? FALLBACK_PALETTE.accents[0];
  return {
    background: background?.id ?? STANDARD_BACKGROUND,
    accent: accent.id,
    backgroundLabel: background?.label ?? "Стандартный",
    canvas: background ? background.hex : null,
    accentLabel: accent.label,
    accentHex: accent.hex,
    accentText: accent.text || WHITE,
  };
}

export interface LookTokens {
  primary: string;
  primaryHover: string;
  primaryActive: string;
  primaryText: string;
  link: string;
  linkHover: string;
  linkActive: string;
  /** The background of a selected menu item and its ink. */
  soft: string;
  softInk: string;
  focus: string;
  canvas: string | null;
  /** A darker secondary text when the default one is below 4.5:1 on this canvas; null — as is. */
  muted: string | null;
}

/**
 * UXW-14: hover and pressed are the accent made darker (the button never gets paler under the finger, so its white
 * label keeps the contrast), the link is the accent darkened until it reads on the canvas and on a white card.
 */
export function lookTokens(look: ResolvedLook): LookTokens {
  const canvas = look.canvas ?? STANDARD_CANVAS;
  const link = readableOn(look.accentHex, [canvas, WHITE]);
  const soft = mix(look.accentHex, WHITE, 0.9);
  const muted = readableOn(MUTED_COLOR, [canvas]);
  return {
    primary: look.accentHex,
    primaryHover: mix(look.accentHex, BLACK, 0.12),
    primaryActive: mix(look.accentHex, BLACK, 0.24),
    primaryText: look.accentText,
    link,
    linkHover: mix(link, BLACK, 0.12),
    linkActive: mix(link, BLACK, 0.24),
    soft,
    softInk: readableOn(look.accentHex, [soft]),
    focus: look.accentHex,
    canvas: look.canvas,
    muted: muted.toLowerCase() === MUTED_COLOR.toLowerCase() ? null : muted,
  };
}

export interface LookProblem {
  code: "text_on_accent" | "accent_on_canvas" | "accent_on_surface" | "text_on_canvas";
  ratio: number;
  min: number;
  text: string;
}

/**
 * UXW-14: what makes a combination unsupported, in words; empty — the look may be shown and saved. Checked: the label
 * of the button in the normal, hover and pressed states (4.5:1), the button, focus ring and border against the
 * canvas and a white card (3:1), the main text on the canvas (4.5:1).
 */
export function lookProblems(look: ResolvedLook): LookProblem[] {
  const canvas = look.canvas ?? STANDARD_CANVAS;
  const t = lookTokens(look);
  const problems: LookProblem[] = [];
  const label = Math.min(...[t.primary, t.primaryHover, t.primaryActive].map((bg) => contrastRatio(t.primaryText, bg)));
  if (label < MIN_TEXT) {
    problems.push({
      code: "text_on_accent",
      ratio: label,
      min: MIN_TEXT,
      text: `Подпись на кнопке цвета «${look.accentLabel}» читается плохо: контраст ${formatRatio(label)}, нужно не меньше 4,5:1. Выберите другой цвет кнопок.`,
    });
  }
  const onCanvas = contrastRatio(t.primary, canvas);
  if (onCanvas < MIN_UI) {
    problems.push({
      code: "accent_on_canvas",
      ratio: onCanvas,
      min: MIN_UI,
      text: `Цвет кнопок «${look.accentLabel}» сливается с фоном «${look.backgroundLabel}»: контраст ${formatRatio(onCanvas)}, нужно не меньше 3:1. Выберите другой фон или цвет кнопок.`,
    });
  }
  const onSurface = contrastRatio(t.primary, WHITE);
  if (onSurface < MIN_UI) {
    problems.push({
      code: "accent_on_surface",
      ratio: onSurface,
      min: MIN_UI,
      text: `Цвет кнопок «${look.accentLabel}» почти не виден на белых карточках: контраст ${formatRatio(onSurface)}, нужно не меньше 3:1. Выберите другой цвет кнопок.`,
    });
  }
  const text = contrastRatio(TEXT_COLOR, canvas);
  if (text < MIN_TEXT) {
    problems.push({
      code: "text_on_canvas",
      ratio: text,
      min: MIN_TEXT,
      text: `Текст на фоне «${look.backgroundLabel}» читается плохо: контраст ${formatRatio(text)}, нужно не меньше 4,5:1. Выберите другой фон.`,
    });
  }
  return problems;
}

/** The palette of a server answer completed by the fallback copy (an old cache may lack an entry). */
export function mergePalette(palette: Partial<Palette> | null | undefined): Palette {
  if (!palette) return FALLBACK_PALETTE;
  const backgrounds = [...(palette.backgrounds ?? [])];
  for (const b of FALLBACK_PALETTE.backgrounds) if (!backgrounds.some((x) => x.id === b.id)) backgrounds.push(b);
  const accents = [...(palette.accents ?? [])];
  for (const a of FALLBACK_PALETTE.accents) if (!accents.some((x) => x.id === a.id)) accents.push(a);
  return { backgrounds, accents };
}

export function sameAppearance(a: WorkspaceAppearance | null | undefined, b: WorkspaceAppearance | null | undefined): boolean {
  return (a?.background ?? null) === (b?.background ?? null) && (a?.accent ?? null) === (b?.accent ?? null);
}
